import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject
} from '@angular/core';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { MaxWidthDirective } from '../../../../shared/directives/max-width.directive';
import { MinWidthDirective } from '../../../../shared/directives/min-width.directive';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { CartItemTypes } from '../../../cart/core/enums/CartItemTypes';
import { ICartItem } from '../../../cart/core/interfaces/ICartItem';
import { CartService } from '../../../cart/core/services/CartService';
import { IKioskPackage, IKioskPackageContent } from '../../core/interfaces/IKioskPackage';
import { IKioskProduct } from '../../core/interfaces/IKioskProduct';
import { cloneDeep } from 'lodash';
import { IPackageProduct } from '../../../cart/core/interfaces/IPackageProduct';
import { finalize } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { ImgFallbackDirective } from '../../../../shared/directives/img-fallback.directive';
import {
  buildPackageContentGroups,
  resolveFixedPackageLeaves
} from './package-selection.utils';

interface IPackageSelectionOption {
  id: number;
  name: string;
  bitmapUrl?: string;
  position?: IKioskProduct['position'];
  selected: boolean;
  product?: IKioskProduct;
  contentPackage?: IKioskPackage;
  nestedLeaves?: IPackageProduct[];
}

interface IPackageSelectionGroup {
  groupingNr: number;
  options: IPackageSelectionOption[];
}

/** Saved parent frame while user configures a nested set (no recursive component instances). */
interface INestedFrame {
  data: IKioskPackage;
  groups: IPackageSelectionGroup[];
  cartItem: ICartItem;
  depth: number;
  shouldAddToCart: boolean;
  pendingGroup: IPackageSelectionGroup;
  pendingOption: IPackageSelectionOption;
}

@Component({
  selector: 'app-package-products-selection-modal',
  standalone: true,
  imports: [
    ModalComponent,
    MinWidthDirective,
    MaxWidthDirective,
    TranslatePipe,
    ImgFallbackDirective
  ],
  templateUrl: './package-products-selection-modal.component.html',
  styleUrl: './package-products-selection-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PackageProductsSelectionModalComponent extends BaseModalComponent<IKioskPackage> {

  private static readonly MAX_DEPTH = 3;

  @Input() shouldAddToCart = true;

  @Output() packageConfirmed = new EventEmitter<IPackageProduct[]>();
  @Output() cancel = new EventEmitter<number>();

  public groups: IPackageSelectionGroup[] = [];
  public readonly environment = environment;

  private _cartItem: ICartItem;
  private _depth = 1;
  private readonly _frameStack: INestedFrame[] = [];
  private readonly _cartService = inject(CartService);

  public override open(val?: IKioskPackage, cartItem?: ICartItem, depth = 1): void {
    this._frameStack.length = 0;
    this.loadPackageFrame(val, cartItem, depth, this.shouldAddToCart, true);
  }

  public get isConfirmButtonDisabled(): boolean {
    return !this.groups.every(group => group.options.some(option =>
      option.selected && (!option.contentPackage || !!option.nestedLeaves?.length)
    ));
  }

  public selectOption(group: IPackageSelectionGroup, option: IPackageSelectionOption): void {
    if (option.contentPackage) {
      this.pushNestedFrame(group, option);
      return;
    }

    if (group.groupingNr === undefined) {
      return;
    }

    group.options.forEach(x => {
      x.selected = false;
      x.nestedLeaves = undefined;
    });
    option.selected = true;
  }

  public confirmPackage(btn: HTMLButtonElement): void {
    if (this._frameStack.length > 0) {
      this.popNestedFrame(this.packageProducts);
      return;
    }

    if (this.shouldAddToCart) {
      this.addPackageToCart(true, btn);
    } else {
      this.packageConfirmed.emit(this.packageProducts);
      this.close();
    }
  }

  public cancelSelection(): void {
    if (this._frameStack.length > 0) {
      this.popNestedFrame(null);
      return;
    }

    this.cancel.emit(this.data?.id);
    this.close();
  }

  private loadPackageFrame(
    pkg: IKioskPackage,
    cartItem: ICartItem | undefined,
    depth: number,
    shouldAddToCart: boolean,
    openModal: boolean
  ): void {
    this._cartItem = cartItem;
    this._depth = depth;
    this.shouldAddToCart = shouldAddToCart;
    this.data = pkg;
    this.groups = [];

    const remainingLeaves = cartItem?.packageProducts
      ? [...cartItem.packageProducts]
      : null;

    this.data.packageContent.forEach(content => {
      const option = this.createOption(content);
      if (!option) {
        return;
      }

      const fGroup = this.groups.find(y => y.groupingNr === content.groupingNr);
      if (fGroup) {
        fGroup.options.push(option);
      } else {
        this.groups.push({
          groupingNr: content.groupingNr,
          options: [option]
        });
      }
    });

    this.groups.forEach(group => {
      if (group.options.length === 1) {
        this.selectAndResolveOption(group, group.options[0], remainingLeaves);
      } else if (group.groupingNr === undefined) {
        group.options.forEach(option => this.selectAndResolveOption(group, option, remainingLeaves));
      } else if (remainingLeaves) {
        this.rematchGroupSelection(group, remainingLeaves);
      }

      group.options.sortByKey('position');
    });

    this.groups.sortByKey('groupingNr');
    this.cdr.detectChanges();

    if (this.canAutoConfirm()) {
      if (this._frameStack.length > 0) {
        this.popNestedFrame(this.packageProducts);
        return;
      }

      if (shouldAddToCart) {
        this.addPackageToCart(false);
      } else {
        this.packageConfirmed.emit(this.packageProducts);
        this.close();
      }
      return;
    }

    if (openModal) {
      super.open(pkg);
    } else {
      this.cdr.detectChanges();
    }
  }

  private pushNestedFrame(group: IPackageSelectionGroup, option: IPackageSelectionOption): void {
    if (!option.contentPackage || this._depth >= PackageProductsSelectionModalComponent.MAX_DEPTH) {
      return;
    }

    this._frameStack.push({
      data: this.data,
      groups: this.groups,
      cartItem: this._cartItem,
      depth: this._depth,
      shouldAddToCart: this.shouldAddToCart,
      pendingGroup: group,
      pendingOption: option
    });

    const rematchCartItem = option.nestedLeaves?.length
      ? { ...(this._cartItem ?? {} as ICartItem), packageProducts: option.nestedLeaves } as ICartItem
      : undefined;

    this.loadPackageFrame(option.contentPackage, rematchCartItem, this._depth + 1, false, false);
  }

  private popNestedFrame(nestedLeaves: IPackageProduct[] | null): void {
    const frame = this._frameStack.pop();
    if (!frame) {
      return;
    }

    this.data = frame.data;
    this.groups = frame.groups;
    this._cartItem = frame.cartItem;
    this._depth = frame.depth;
    this.shouldAddToCart = frame.shouldAddToCart;

    if (nestedLeaves) {
      const group = frame.pendingGroup;
      const option = frame.pendingOption;

      if (group.groupingNr !== undefined) {
        group.options.forEach(x => {
          x.selected = false;
          x.nestedLeaves = undefined;
        });
      }

      option.selected = true;
      option.nestedLeaves = nestedLeaves;
    }

    this.cdr.detectChanges();
  }

  private createOption(content: IKioskPackageContent): IPackageSelectionOption | null {
    if (content.contentPackage) {
      const nestedPackage = cloneDeep(content.contentPackage);

      return {
        id: nestedPackage.id,
        name: nestedPackage.displayName || nestedPackage.name,
        bitmapUrl: nestedPackage.bitmapUrl,
        position: nestedPackage.position,
        selected: false,
        contentPackage: nestedPackage
      };
    }

    if (!content.product) {
      return null;
    }

    const product = cloneDeep(content.product);

    return {
      id: product.id,
      name: product.name,
      bitmapUrl: product.bitmapUrl,
      position: product.position,
      selected: false,
      product
    };
  }

  private selectAndResolveOption(
    group: IPackageSelectionGroup,
    option: IPackageSelectionOption,
    remainingLeaves: IPackageProduct[] | null
  ): void {
    if (option.contentPackage) {
      if (remainingLeaves) {
        const matched = this.takeLeavesForPackage(option.contentPackage, remainingLeaves);
        if (matched) {
          option.selected = true;
          option.nestedLeaves = matched;
          return;
        }
      }

      const fixedLeaves = this.tryResolveFixedLeaves(option.contentPackage);
      if (fixedLeaves) {
        option.selected = true;
        option.nestedLeaves = fixedLeaves;
      }

      return;
    }

    option.selected = true;

    if (option.product && remainingLeaves) {
      this.takeLeaf(remainingLeaves, option.product.id, group.groupingNr);
    }
  }

  private rematchGroupSelection(group: IPackageSelectionGroup, remainingLeaves: IPackageProduct[]): void {
    for (const option of group.options) {
      if (option.contentPackage) {
        const matched = this.takeLeavesForPackage(option.contentPackage, remainingLeaves);
        if (matched) {
          option.selected = true;
          option.nestedLeaves = matched;
          return;
        }
      } else if (option.product) {
        const leaf = this.takeLeaf(remainingLeaves, option.product.id, group.groupingNr);
        if (leaf) {
          option.selected = true;
          return;
        }
      }
    }
  }

  private canAutoConfirm(): boolean {
    const structureAllowsAuto =
      this.groups.every(x => x.options.length === 1) ||
      (this.groups.length === 1 && this.groups[0].groupingNr === undefined);

    if (!structureAllowsAuto) {
      return false;
    }

    return this.groups.every(group => group.options.every(option =>
      !option.contentPackage || !!option.nestedLeaves?.length
    ));
  }

  private tryResolveFixedLeaves(pkg: IKioskPackage, depth = this._depth + 1): IPackageProduct[] | null {
    return resolveFixedPackageLeaves(pkg, depth, PackageProductsSelectionModalComponent.MAX_DEPTH);
  }

  private takeLeavesForPackage(
    pkg: IKioskPackage,
    remainingLeaves: IPackageProduct[]
  ): IPackageProduct[] | null {
    const snapshot = [...remainingLeaves];
    const matched: IPackageProduct[] = [];
    const groups = buildPackageContentGroups(pkg);

    for (const group of groups) {
      if (group.groupingNr === undefined) {
        for (const content of group.contents) {
          const taken = this.takeContentLeaves(content, remainingLeaves, group.groupingNr);
          if (!taken) {
            remainingLeaves.splice(0, remainingLeaves.length, ...snapshot);
            return null;
          }
          matched.push(...taken);
        }
        continue;
      }

      let groupMatched = false;

      for (const content of group.contents) {
        const attempt = [...remainingLeaves];
        const taken = this.takeContentLeaves(content, attempt, group.groupingNr);
        if (taken) {
          remainingLeaves.splice(0, remainingLeaves.length, ...attempt);
          matched.push(...taken);
          groupMatched = true;
          break;
        }
      }

      if (!groupMatched) {
        remainingLeaves.splice(0, remainingLeaves.length, ...snapshot);
        return null;
      }
    }

    return matched;
  }

  private takeContentLeaves(
    content: IKioskPackageContent,
    remainingLeaves: IPackageProduct[],
    groupingNr: number
  ): IPackageProduct[] | null {
    if (content.contentPackage) {
      return this.takeLeavesForPackage(content.contentPackage, remainingLeaves);
    }

    if (!content.product) {
      return null;
    }

    const leaf = this.takeLeaf(remainingLeaves, content.product.id, groupingNr);
    return leaf ? [leaf] : null;
  }

  private takeLeaf(
    remainingLeaves: IPackageProduct[],
    productId: number,
    groupingNr: number
  ): IPackageProduct | null {
    const index = remainingLeaves.findIndex(leaf =>
      leaf.id === productId && leaf.groupingNr === groupingNr
    );

    if (index === -1) {
      return null;
    }

    const [leaf] = remainingLeaves.splice(index, 1);
    return leaf;
  }

  private addPackageToCart(isPackageEditable: boolean, btn?: HTMLButtonElement): void {
    const packageProducts = this.packageProducts;

    btn?.classList.add('disabled');

    if (this._cartItem) {
      this._cartItem.packageProducts = packageProducts;

      this._cartService.updateCartItem([this._cartItem])
        .pipe(
          finalize(() => btn?.classList.remove('disabled'))
        )
        .subscribe({
          next: () => this.close(),
          error: ({ error }: ApiErrorResponse) => {
            this.notificationService.pushError(
              'Fehler'.trans('notifications/error.Title'),
              error.message.trans(`update-cart-package-item/notifications/${error.message}.Message`)
            );
          }
        });
    } else {
      const itemToAdd: Partial<ICartItem> = {
        itemId: this.data.id,
        itemType: CartItemTypes.Package,
        quantity: 1,
        packageProducts,
        flagPackageEditable: isPackageEditable
      };

      this._cartService.addConcessionItemToCart(itemToAdd)
        .pipe(
          finalize(() => btn?.classList.remove('disabled'))
        )
        .subscribe({
          next: () => this.close(),
          error: ({ error }: ApiErrorResponse) => {
            this.notificationService.pushError(
              'Fehler'.trans('notifications/error.Title'),
              error.message.trans(`add-package-item-to-cart/notifications/${error.message}.Message`)
            );
          }
        });
    }
  }

  private get packageProducts(): IPackageProduct[] {
    const packageProducts: IPackageProduct[] = [];

    for (const group of this.groups) {
      for (const option of group.options) {
        if (!option.selected) {
          continue;
        }

        if (option.contentPackage) {
          if (option.nestedLeaves?.length) {
            packageProducts.push(...option.nestedLeaves);
          }
        } else if (option.product) {
          packageProducts.push({
            id: option.product.id,
            name: option.product.name,
            groupingNr: group.groupingNr
          });
        }
      }
    }

    return packageProducts;
  }
}
